import { useEffect, useState } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, db } from './config';
import { FUNCTIONS_REGION } from './region';
import { compareRecitation, type Comparison, type HeardWord, type PracticeHint } from '../practice/compare';

/**
 * The self-practice loop, from the client's side — المرحلة 6.
 *
 * The comparison runs HERE rather than on the server, and deliberately: the
 * app already carries the Quran text (bundledQuran), the server does not, and
 * م-6-03 settles the trust question — a hint is not a record. Nothing this
 * writes can become an error against a child, so the child's own device is
 * allowed to author it. The rules keep one child out of another's document,
 * which is the only thing that has to be enforced.
 *
 * The server's only job is hearing: audio in, words out.
 */

const fns = () => getFunctions(app, FUNCTIONS_REGION);

/** How many divergences a stored practice keeps. The rules cap this too. */
const MAX_WEAK = 30;

export type Transcription =
  | { available: true; words: HeardWord[] }
  | { available: false; reason: string };

export type StoredPractice = {
  studentId: string;
  surah: number;
  ayah: number;
  at: number;
  correct: number;
  total: number;
  flagged: number;
  unreliable: boolean;
  weak: PracticeHint[];
};

/** Audio in, words out. Never throws for a service that is merely absent. */
export async function transcribeRecitation(input: {
  audio: string;
  mimeType: string;
  durationMs: number;
}): Promise<Transcription> {
  const call = httpsCallable<typeof input, Transcription>(fns(), 'transcribeRecitation');
  try {
    return (await call(input)).data;
  } catch {
    /*
     * م-6-06: an unreachable callable is the same event as an unreachable
     * recogniser — the practice becomes self-review, and the screen carries
     * on. Only a caller's own mistake (audio too long, not signed in) would
     * throw here, and treating that as "unavailable" too costs nothing: the
     * child sees the āya and reads it themselves either way.
     */
    return { available: false, reason: 'unreachable' };
  }
}

/**
 * What gets kept: counts, and the divergent positions — never the audio, and
 * never the whole transcript. One document per child, overwritten, because
 * م-6-02 asks for the LAST practice and a history of a machine's guesses about
 * a child is not something worth keeping.
 */
export function practiceSummary(input: {
  studentId: string;
  surah: number;
  ayah: number;
  at: number;
  comparison: Comparison;
}): StoredPractice {
  const { studentId, surah, ayah, at, comparison } = input;
  return {
    studentId,
    surah,
    ayah,
    at,
    correct: comparison.correct,
    total: comparison.hints.length,
    flagged: comparison.flagged,
    unreliable: comparison.unreliable,
    // An unreliable take keeps no positions at all: they would be a list of
    // words the child probably said correctly.
    weak: comparison.unreliable
      ? []
      : comparison.hints
        .filter((h) => h.kind === 'changed' || h.kind === 'skipped')
        .slice(0, MAX_WEAK),
  };
}

/** Saves the last practice. Failure is not worth breaking the screen over. */
export async function savePractice(summary: StoredPractice): Promise<boolean> {
  try {
    await setDoc(doc(db, 'practices', summary.studentId), summary);
    return true;
  } catch {
    return false;
  }
}

/** The last practice, for the child themself, their family, or their معلم. */
export function useLastPractice(uid: string | null): {
  practice: StoredPractice | null; loading: boolean;
} {
  const [state, setState] = useState<{ practice: StoredPractice | null; loading: boolean }>(
    { practice: null, loading: true },
  );

  useEffect(() => {
    if (!uid) { setState({ practice: null, loading: false }); return undefined; }
    setState({ practice: null, loading: true });
    return onSnapshot(
      doc(db, 'practices', uid),
      (snap) => {
        const data = snap.exists() ? (snap.data() as Partial<StoredPractice>) : null;
        setState({
          practice: data && typeof data.surah === 'number' && typeof data.ayah === 'number'
            ? {
              studentId: uid,
              surah: data.surah,
              ayah: data.ayah,
              at: typeof data.at === 'number' ? data.at : 0,
              correct: typeof data.correct === 'number' ? data.correct : 0,
              total: typeof data.total === 'number' ? data.total : 0,
              flagged: typeof data.flagged === 'number' ? data.flagged : 0,
              unreliable: data.unreliable === true,
              weak: Array.isArray(data.weak) ? data.weak : [],
            }
            : null,
          loading: false,
        });
      },
      // A معلم who may not read this pupil gets "no practice", not an error.
      () => setState({ practice: null, loading: false }),
    );
  }, [uid]);

  return state;
}

/**
 * One take, end to end: hear it, compare it, keep the summary.
 *
 * Returns the comparison so the screen can draw it, and `available: false`
 * when the service could not be asked — which is م-6-06's fork, not an error.
 */
export async function runPractice(input: {
  studentId: string;
  surah: number;
  ayah: number;
  expected: string[];
  audio: string;
  mimeType: string;
  durationMs: number;
  now: number;
}): Promise<
  | { available: true; comparison: Comparison; saved: boolean }
  | { available: false; reason: string }
> {
  const heard = await transcribeRecitation({
    audio: input.audio, mimeType: input.mimeType, durationMs: input.durationMs,
  });
  if (!heard.available) return heard;

  const comparison = compareRecitation({ expected: input.expected, heard: heard.words });
  const saved = await savePractice(practiceSummary({
    studentId: input.studentId,
    surah: input.surah,
    ayah: input.ayah,
    at: input.now,
    comparison,
  }));
  return { available: true, comparison, saved };
}
